/**
 * Log in as… — the picker's pure rules (Rule Book §3.13, owner 2026-10-07:
 * "no where is showing login as option").
 *
 * Kept out of the .tsx so a test can read them without a DOM. The picker
 * offers only what the API would accept: a kind the viewer holds the
 * capability for, an operator that is not deleted, and a person the start
 * route would find active. The API checks every one of these again.
 */
import type { Abilities } from '../entity-actions.ts';

export type LoginAsKind = 'OPERATOR_USER' | 'BRAND_USER' | 'AFFILIATE';

/**
 * What the picker's "Log in as a" lists: the three API kinds plus SELF, the
 * viewer entering a console as themselves (owner, 2026-10-13). SELF has no
 * target and no reason, and never leads to the affiliate console.
 */
export type PickerKind = LoginAsKind | 'SELF';

/**
 * The kinds the viewer may log in as, in the order the picker lists them.
 * SELF comes first when offered: the operator picker needs
 * login_as.operator_user, a brand's picker (`onBrand`) login_as.brand_user.
 */
export function loginAsKinds(can: Abilities, onBrand = false): PickerKind[] {
  const out: PickerKind[] = [];
  if (onBrand ? can.loginAs.brandUser : can.loginAs.operatorUser) out.push('SELF');
  if (can.loginAs.operatorUser) out.push('OPERATOR_USER');
  if (can.loginAs.brandUser) out.push('BRAND_USER');
  if (can.loginAs.affiliate) out.push('AFFILIATE');
  return out;
}

/** Whether "Log in as…" shows at all: at least one kind is granted. */
export function mayLogInAsAnyone(can: Abilities): boolean {
  return loginAsKinds(can).length > 0;
}

/**
 * Whether an operator's actions panel offers "Log in as…". The start route
 * refuses a deleted operator (there is nobody left to log in as), so a
 * deleted or purged one does not offer it.
 */
export function operatorOffersLoginAs(state: string, can: Abilities): boolean {
  return mayLogInAsAnyone(can) && state !== 'SOFT_DELETED' && state !== 'PURGED';
}

/**
 * Whether a brand's actions panel offers "Log in as…" (owner, 2026-10-13).
 * A deleted or purged brand has no console to open, and deleting an operator
 * leaves its brands live while the start route refuses the operator, so the
 * operator's own rule applies too. An unknown operator state is not a refusal.
 * A switched-off brand does not offer it either (owner, 2026-10-13); the start
 * route would still accept one, so this is the screen's rule, not the API's.
 */
export function brandOffersLoginAs(
  standing: { deleted: boolean; purged: boolean },
  brandStatus: string,
  operatorState: string | undefined,
  can: Abilities,
): boolean {
  return operatorOffersLoginAs(operatorState ?? 'LIVE', can)
    && !standing.deleted && !standing.purged && brandStatus === 'ACTIVE';
}

/** Operators a login-as can be started on: every one not deleted, by name. */
export function operatorChoices(
  rows: readonly { id: string; display_name: string; lifecycle_state: string }[],
): { id: string; name: string }[] {
  return rows
    .filter((o) => o.lifecycle_state !== 'SOFT_DELETED' && o.lifecycle_state !== 'PURGED')
    .map((o) => ({ id: o.id, name: o.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface LoginAsTarget {
  /** The user id for a staff member, the affiliate profile id for an affiliate. */
  readonly id: string;
  readonly name: string;
  /** What tells two people of the same name apart: an email. */
  readonly detail: string;
  /** A brand user's brands; one opens straight away, several are picked from. */
  readonly brands: readonly { readonly id: string; readonly name: string }[];
}

interface StaffRow {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  readonly status: string;
  readonly brands: readonly { readonly id: string; readonly name: string }[];
}

/**
 * An operator's team, cut to the members the start route would accept for
 * this kind: active accounts only (a locked or disabled one answers
 * TARGET_NOT_ACTIVE), operator users without a brand limit, brand users
 * with one.
 */
export function staffTargets(items: readonly StaffRow[], kind: 'OPERATOR_USER' | 'BRAND_USER'): LoginAsTarget[] {
  return items
    .filter((m) => m.status === 'ACTIVE')
    .filter((m) => (kind === 'BRAND_USER' ? m.brands.length > 0 : m.brands.length === 0))
    .map((m) => ({
      id: m.id,
      name: m.name?.trim() || m.email,
      detail: m.email,
      brands: m.brands,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

interface AffiliateRow {
  readonly id: string;
  readonly legal_name: string;
  readonly email: string;
  readonly status: string;
  readonly deleted?: boolean;
  readonly purged?: boolean;
}

/** An operator's affiliates the start route would find: not deleted, not rejected or terminated. */
export function affiliateTargets(items: readonly AffiliateRow[]): LoginAsTarget[] {
  return items
    .filter((a) => !a.deleted && !a.purged && a.status !== 'REJECTED' && a.status !== 'TERMINATED')
    .map((a) => ({ id: a.id, name: a.legal_name, detail: a.email, brands: [] }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The picker opened from one brand's popup: brand users who work on that
 * brand, each opening its console straight away (no brand to pick), and
 * affiliates enrolled on it. Operator users are not limited to a brand, so
 * every one of them is offered unchanged.
 */
export function brandUserTargets(
  items: readonly StaffRow[],
  brand: { readonly id: string; readonly name: string },
): LoginAsTarget[] {
  return staffTargets(items, 'BRAND_USER')
    .filter((m) => m.brands.some((b) => b.id === brand.id))
    .map((m) => ({ ...m, brands: [brand] }));
}

export function brandAffiliateTargets(
  items: readonly (AffiliateRow & { readonly brands: readonly { readonly brand_id: string }[] })[],
  brandId: string,
): LoginAsTarget[] {
  return affiliateTargets(items.filter((a) => a.brands.some((b) => b.brand_id === brandId)));
}

/**
 * What the code box holds, as the API reads it: the digits only, so a code
 * pasted as "123 456" is the same code. Six digits, never more.
 */
export function cleanCode(typed: string): string {
  return typed.replace(/\D+/g, '').slice(0, 6);
}

/** Whether the box holds a whole code (the start button waits for one). */
export function isWholeCode(typed: string): boolean {
  return cleanCode(typed).length === 6;
}

/** The body POST /v1/platform/login-as takes, exactly as the row buttons send it. */
export function startBody(input: {
  operatorId: string;
  kind: PickerKind;
  reason: string;
  /** A fresh code from the viewer's own authenticator app, every start (owner, 2026-10-16). */
  code: string;
  targetUserId?: string | undefined;
  affiliateId?: string | undefined;
  brandId?: string | undefined;
}): Record<string, string> {
  const code = cleanCode(input.code);
  // SELF carries no reason or target; its brand_id is the brand picker's brand.
  if (input.kind === 'SELF') {
    return {
      operator_id: input.operatorId,
      target_kind: 'SELF',
      code,
      ...(input.brandId ? { brand_id: input.brandId } : {}),
    };
  }
  return {
    operator_id: input.operatorId,
    target_kind: input.kind,
    reason: input.reason.trim(),
    code,
    ...(input.targetUserId ? { target_user_id: input.targetUserId } : {}),
    ...(input.affiliateId ? { affiliate_id: input.affiliateId } : {}),
    ...((input.kind === 'BRAND_USER' || input.kind === 'OPERATOR_USER') && input.brandId
      ? { brand_id: input.brandId } : {}),
  };
}

/** The API's floor for a reason (REASON_TOO_SHORT below it). */
export const MIN_REASON = 10;

/** What still stops the picker's start button, or null when it may go. */
export function pickerBlocker(p: {
  /** Absent means a named person is being logged in as. */
  kind?: PickerKind;
  operatorId: string;
  targetId: string;
  needsBrand: boolean;
  brandId: string;
  reason: string;
  code: string;
}): 'operator' | 'target' | 'brand' | 'reason' | 'code' | null {
  if (!p.operatorId) return 'operator';
  if (p.kind !== 'SELF') {
    if (!p.targetId) return 'target';
    if (p.needsBrand && !p.brandId) return 'brand';
    if (p.reason.trim().length < MIN_REASON) return 'reason';
  }
  if (!isWholeCode(p.code)) return 'code';
  return null;
}

/**
 * The start route's three refusals about the code, as the dialog words them.
 * Anything else is the API's own message.
 *
 *   code     — wrong, or already used: the box is cleared and asks for the next one
 *   missing  — none was sent (the button waits for one, so a stale screen)
 *   enrol    — the viewer has no authenticator app: no code will ever work
 */
export type CodeRefusal = 'code' | 'missing' | 'enrol';

export function codeRefusal(failure: unknown): CodeRefusal | null {
  // An ApiClientError carries the API's code on itself (portal-kit client.ts).
  const code = (failure as { code?: unknown } | null)?.code;
  if (code === 'MFA_CODE_INVALID') return 'code';
  if (code === 'MFA_CODE_REQUIRED') return 'missing';
  if (code === 'MFA_ENROLMENT_REQUIRED') return 'enrol';
  return null;
}
